import type { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBan,
  faBullseye,
  faChartBar,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faClipboard,
  faColumns,
  faCompress,
  faCompressAlt,
  faCopy,
  faDatabase,
  faDownload,
  faExpand,
  faExpandArrowsAlt,
  faEye,
  faFileAlt,
  faFileCode,
  faFileCsv,
  faFileImage,
  faFolder,
  faGaugeHigh,
  faImage,
  faInbox,
  faKey,
  faLayerGroup,
  faListUl,
  faLock,
  faMapSigns,
  faMaximize,
  faMinimize,
  faMinus,
  faParagraph,
  faPencilAlt,
  faPlay,
  faPlus,
  faRandom,
  faRoute,
  faSearch,
  faShareAlt,
  faSitemap,
  faStepBackward,
  faStepForward,
  faSyncAlt,
  faTags,
  faThumbtack,
  faTimes,
  faTimesCircle,
  faTrashAlt,
  faUndo,
  faUnlock
} from '@fortawesome/free-solid-svg-icons';

/** Map icon names → Font Awesome solid icons */
const ICONS = {
  'arrow-counterclockwise': faUndo,
  'arrow-repeat': faSyncAlt,
  'arrows-angle-contract': faCompressAlt,
  'arrows-collapse': faMinimize,
  'arrows-expand': faMaximize,
  'aspect-ratio': faExpandArrowsAlt,
  'bar-chart': faChartBar,
  bullseye: faBullseye,
  check2: faCheck,
  'check2-circle': faCheckCircle,
  'chevron-down': faChevronDown,
  clipboard: faClipboard,
  copy: faCopy,
  'dash-lg': faMinus,
  database: faDatabase,
  'diagram-3': faSitemap,
  download: faDownload,
  eye: faEye,
  'file-earmark-code': faFileCode,
  'file-text': faFileAlt,
  'filetype-csv': faFileCsv,
  'filetype-jpg': faFileImage,
  'filetype-json': faFileCode,
  'filetype-png': faFileImage,
  'filetype-svg': faFileCode,
  folder2: faFolder,
  fullscreen: faExpand,
  'fullscreen-exit': faCompress,
  image: faImage,
  inbox: faInbox,
  key: faKey,
  layers: faLayerGroup,
  'list-ul': faListUl,
  lock: faLock,
  'lock-fill': faLock,
  pencil: faPencilAlt,
  'pin-angle': faThumbtack,
  'pin-angle-fill': faThumbtack,
  'play-btn': faPlay,
  'plus-lg': faPlus,
  search: faSearch,
  trash: faTrashAlt,
  share: faShareAlt,
  shuffle: faRandom,
  signpost: faMapSigns,
  'signpost-split': faRoute,
  'skip-backward': faStepBackward,
  'skip-forward': faStepForward,
  'slash-circle': faBan,
  speedometer2: faGaugeHigh,
  subtract: faColumns,
  tags: faTags,
  'text-paragraph': faParagraph,
  unlock: faUnlock,
  'x-circle': faTimesCircle,
  'x-lg': faTimes
} as const satisfies Record<string, IconDefinition>;

export type IconName = keyof typeof ICONS;

type IconProps = {
  name: IconName | string;
  className?: string;
  size?: string | number;
  style?: CSSProperties;
};

export default function Icon({
  name,
  className = '',
  size,
  style
}: IconProps) {
  const icon = ICONS[name as IconName];
  if (!icon) return null;
  return (
    <FontAwesomeIcon
      icon={icon}
      className={className}
      style={{
        lineHeight: 1,
        verticalAlign: 'middle',
        ...(size ? { fontSize: size } : null),
        ...style
      }}
      fixedWidth
      aria-hidden="true"
    />
  );
}
